/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect } from 'react';
import AdminLoginView from './views/admin/AdminLoginView';
import AdminOverview from './views/admin/AdminOverview';
import AdminInventory from './views/admin/AdminInventory';
import AdminSales from './views/admin/AdminSales';
import AdminInvestors from './views/admin/AdminInvestors';
import AdminEmployees from './views/admin/AdminEmployees';

import HomeView from './views/public/HomeView';
import ListingsView from './views/public/ListingsView';

import Icons from './views/component/Icons';
import CarDetailsModal from "./ViewModel/component/CarDetailsModal";
import Car from './models/Car';
import { loadCars } from './lib/stock';
import { YearSummary, type StaffSessionUser } from '@lumora/core';
import { aboutContent, businessSummary as summaryVm } from './lib/about';
import { staffSession } from './lib/vms';
import type { AboutData } from './models/About';

import AssociateScheduleView from './views/component/AssociateScheduleView';
import NotificationBell, { type AppointmentNotification } from './views/component/NotificationBell';

import AboutView from './views/public/AboutView';
import DisclaimerView from './views/public/DisclaimerView';
import { StaffDashboard } from './pages/StaffPages';

type AuthUser = StaffSessionUser;

const hasPermission = (user: AuthUser | null, permission: string) => {
    return !!user && user.can(permission);
};

function useStaffHashRoute(): boolean {
    const [onStaffRoute, setOnStaffRoute] = useState(() =>
        typeof window !== 'undefined' && window.location.hash.startsWith('#/staff'),
    );
    useEffect(() => {
        const sync = () => setOnStaffRoute(window.location.hash.startsWith('#/staff'));
        window.addEventListener('hashchange', sync);
        return () => window.removeEventListener('hashchange', sync);
    }, []);
    return onStaffRoute;
}

export default function App() {
    const staffHashRoute = useStaffHashRoute();
    const [view, setView] = useState('home');
    const [authUser, setAuthUser] = useState<AuthUser | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedCar, setSelectedCar] = useState<Car | null>(null);
    const [isCarEditOverride, setIsCarEditOverride] = useState(false);
    const [inventory, setInventory] = useState<Car[]>([]);
    const [businessSummary, setBusinessSummary] = useState<YearSummary>(YearSummary.empty());
    const [siteData, setSiteData] = useState<AboutData | null>(null);
    const [calendarFocus, setCalendarFocus] = useState<AppointmentNotification | null>(null);
    const isAdmin = hasPermission(authUser, 'admin:access');
    const canManageListings = hasPermission(authUser, 'listings:write');
    const canEditSiteContent = hasPermission(authUser, 'content:write');
    const isSiteEditMode = canEditSiteContent && isEditMode;
    const isStaff = !!authUser;
    const canEditFinancials = hasPermission(authUser, 'admin:access');

    const fetchSiteContent = async () => {
        const data = await aboutContent.load();
        if (data) setSiteData(data);
        else console.error("Failed to load site content:", aboutContent.state.error);
    };

    const fetchInventory = async () => {
        const cars = await loadCars();
        if (cars) setInventory(cars);
    };

    const fetchBusinessSummary = async () => {
        if (await summaryVm.loadSummary()) setBusinessSummary(summaryVm.state.summary ?? YearSummary.empty());
        else console.error("Failed to load business summary:", summaryVm.state.error);
    };

    const saveAboutData = async (updatedContent: AboutData) => {
        setSiteData(updatedContent);
        const ok = await aboutContent.save(updatedContent);
        if (!ok) {
            console.error("Failed to save content:", aboutContent.state.error);
            if (aboutContent.state.data) setSiteData(aboutContent.state.data);
        }
        return ok;
    };

    useEffect(() => {
        const checkAuthStatus = () => {
            const user = staffSession.check();
            setAuthUser(user);
            if (user) {
                fetchInventory();
                if (hasPermission(user, 'business:read')) fetchBusinessSummary();
            }
        };

        checkAuthStatus();
        fetchInventory();
        fetchSiteContent();
    }, []);

    const navigate = (page: string) => {
        setView(page);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);
    };

    const openNotificationTarget = (notification: AppointmentNotification) => {
        if (notification.type === 'sale_request') {
            navigate('admin-sales');
            return;
        }
        setCalendarFocus(notification);
        navigate('associate-calendar');
    };

    const openCarDetails = (car: Car) => {
        setIsCarEditOverride(false);
        setSelectedCar(car);
    };

    const openCarEditor = (car: Car) => {
        setIsCarEditOverride(true);
        setSelectedCar(car);
    };

    const closeCarDetails = () => {
        setSelectedCar(null);
        setIsCarEditOverride(false);
    };

    const navLinks = ['home', 'listings', 'finance', 'about', 'disclaimer'];

    if (staffHashRoute) {
        return <StaffDashboard />;
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900 overflow-x-hidden">
            {isStaff && (
                <div className="bg-red-600 text-white text-[10px] font-black uppercase tracking-widest py-3 px-6 flex justify-between items-center z-50 sticky top-0 shadow-lg">
                    <div className="flex items-center space-x-6">
                        <span className="bg-white text-red-600 px-2 py-0.5 rounded italic">{isAdmin ? 'Admin' : 'Staff'}</span>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => { window.location.hash = '#/staff'; }}
                                className="hover:underline"
                            >
                                Staff
                            </button>
                        )}
                        {isAdmin && <button type="button" onClick={() => navigate('admin-overview')} className="hover:underline">Dashboard</button>}
                        {canManageListings && <button type="button" onClick={() => navigate('admin-inventory')} className="hover:underline">Stock</button>}
                        <button type="button" onClick={() => navigate('associate-calendar')} className="hover:underline">Calendar</button>
                        {isAdmin && (
                            <>
                                <button type="button" onClick={() => navigate('admin-sales')} className="hover:underline">Money</button>
                                <button type="button" onClick={() => navigate('admin-investors')} className="hover:underline">Investors</button>
                                <button type="button" onClick={() => navigate('admin-employees')} className="hover:underline">Team</button>
                            </>
                        )}
                    </div>
                    <div className="flex items-center space-x-4">
                        <NotificationBell onNotificationClick={openNotificationTarget} />
                        {canEditSiteContent && (
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input type="checkbox" checked={isEditMode} onChange={e => setIsEditMode(e.target.checked)} className="rounded text-gray-900 focus:ring-0" />
                                <span>Edit Mode</span>
                            </label>
                        )}
                        <button type="button" onClick={async () => { 
                            staffSession.signOut();
                            setIsEditMode(false);
                            setAuthUser(null); 
                            fetchInventory();
                            navigate('home'); 
                        }} className="opacity-70 hover:opacity-100">Exit</button>
                    </div>
                </div>
            )}

            <nav className="bg-white border-b border-gray-100 sticky top-0 md:relative z-40 h-20 flex items-center shadow-sm">
                <div className="max-w-7xl mx-auto px-4 w-full flex justify-between items-center">
                    <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('home')}>
                        <span className="text-3xl font-black italic tracking-tighter uppercase"><span className="text-red-600">Al Amir</span> Auto</span>
                    </div>
                    <div className="hidden lg:flex space-x-10 items-center">
                        {navLinks.map(p => (
                            <button 
                                key={p} 
                                onClick={() => navigate(p)} 
                                className={`text-[10px] font-black uppercase tracking-widest hover:text-red-600 transition ${view === p ? 'text-red-600' : 'text-gray-500'}`}
                            >
                                {p === 'listings' ? 'Inventory' : p}
                            </button>
                        ))}
                        {!isStaff && (
                            <button onClick={() => navigate('admin-login')} className="bg-gray-900 text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition shadow-lg shadow-gray-200">Staff</button>
                        )}
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-gray-900"><Icons.Menu /></button>
                </div>
                
                {isMobileMenuOpen && (
                    <div className="lg:hidden fixed inset-0 top-20 bg-white z-[100] p-6 animate-in slide-in-from-right duration-300">
                        <div className="flex flex-col space-y-8">
                            {navLinks.map(p => (
                                <button key={p} onClick={() => navigate(p)} className="text-3xl font-black italic uppercase tracking-tighter text-left border-b border-gray-100 pb-2">
                                    {p === 'listings' ? 'Inventory' : p}
                                </button>
                            ))}
                            <button onClick={() => navigate('admin-login')} className="text-xl font-bold text-red-600 text-left">Staff Portal</button>
                        </div>
                    </div>
                )}
            </nav>

            <main className="flex-grow flex flex-col">
                {view === 'home' && (
                    <HomeView 
                        content={siteData} 
                        setContent={saveAboutData} 
                        isEditMode={isSiteEditMode} 
                        inventory={inventory} 
                        navigate={navigate} 
                        onSelectCar={openCarDetails} 
                    />
                )}
                {view === 'listings' && <ListingsView inventory={inventory} onSelectCar={openCarDetails} />}
                {view === 'finance' && (
                    <div className="max-w-4xl mx-auto px-4 py-16 w-full flex-grow text-center">
                        <div className="flex items-center justify-center space-x-4 mb-8 text-red-600">
                            <div className="p-4 bg-red-50 rounded-full"><Icons.Dollar /></div>
                        </div>
                        <h1 className="text-4xl font-bold mb-8">Vehicle Financing</h1>
                        <div className="bg-white p-8 md:p-12 rounded-xl shadow-sm border border-gray-100 text-lg text-gray-700 leading-relaxed">
                            <p className="mb-10">
                                We partner with trusted third-party financial institutions like Marine Credit Union to provide you with competitive and flexible financing options.
                            </p>
                            <a 
                                href="https://marinecreditunion.my.site.com/REG/s/registration?PartnerCode=001PI00000k5Wso" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-block bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest py-4 px-10 rounded shadow-lg transform transition hover:-translate-y-1"
                            >
                                Apply for Credit Now
                            </a>
                        </div>
                    </div>
                )}                
                {view === 'about' && <AboutView content={siteData} navigate={navigate} isEditMode={isSiteEditMode} setContent={saveAboutData} />}
                {view === 'disclaimer' && <DisclaimerView content={siteData} isEditMode={isSiteEditMode} setContent={saveAboutData} />}
                
                {view === 'admin-login' && <AdminLoginView onAuthenticated={(user) => {
                    setAuthUser(user);
                    fetchInventory();
                    if (hasPermission(user, 'business:read')) fetchBusinessSummary();
                    navigate(hasPermission(user, 'admin:access') ? 'admin-overview' : 'associate-calendar');
                }} navigate={navigate} />}
                {view === 'admin-overview' && <AdminOverview summary={businessSummary} />}
                {view === 'admin-inventory' && <AdminInventory inventory={inventory} setInventory={setInventory} onSelectCar={openCarDetails} onEditCar={openCarEditor} isAdmin={isAdmin} />}
                {view === 'associate-calendar' && <AssociateScheduleView inventory={inventory} onOpenCar={openCarDetails} isAdmin={isAdmin} focusAppointment={calendarFocus} />}
                {view === 'admin-sales' && <AdminSales inventory={inventory} />}
                {view === 'admin-investors' && <AdminInvestors />}
                {view === 'admin-employees' && <AdminEmployees />}
            </main>

            {selectedCar && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 sm:p-8">
                    <div className="w-full max-w-1xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ring-1 ring-gray-200">
                        <CarDetailsModal 
                            car={selectedCar} 
                            onClose={closeCarDetails} 
                            isAdmin={isAdmin}
                            isStaff={isStaff}
                            canEditFinancials={canEditFinancials}
                            isAdminEditMode={isSiteEditMode || isCarEditOverride} 
                            refreshInventory={fetchInventory} 
                        />
                    </div>
                </div>
            )}

            <footer className="bg-gray-900 text-white py-20 px-4">
                <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
                    <h2 className="text-4xl font-black italic tracking-tighter uppercase mb-2"><span className="text-red-600">Al Amir</span> Auto</h2>
                    <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mb-10">Premium Pre-Owned Vehicles</p>
                    <div className="flex space-x-10 mb-10 text-[10px] font-black uppercase tracking-widest text-gray-400">
                        <span>{siteData?.footer?.address?.value || "295 37th St NE, Rochester, MN 55906"}</span>
                        <span>{siteData?.contact?.phoneSales?.value || "(507) 850-5048"}</span>
                    </div>
                    <p className="text-[10px] text-gray-600 uppercase font-bold">&copy; {new Date().getFullYear()} Al Amir Auto Sales. All Rights Reserved.</p>
                </div>
            </footer>
        </div>
    );
}
